import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { CartActions } from '@next/core-lib/cart';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { CustomerStatus, SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Store, select } from '@ngrx/store';
import { EMPTY, catchError, finalize, map, mergeMap, of, startWith, switchMap, withLatestFrom } from 'rxjs';

import { AppState } from 'src/app/shared/models';

import { getYear } from 'src/app/store/selectors';

import { Environment } from 'src/environment';

import { getCustomerData } from '../../../store/actions';
 
import {
  ConfirmFertilisersOrderDialogComponent,
  ConfirmFertilisersOrderDialogData,
} from '../../components/confirm-fertilisers-order-dialog/confirm-fertilisers-order-dialog/confirm-fertilisers-order-dialog.component';
import { FertiliserOrderplan } from '../../models';
import { FertilisersApiService } from '../../services/api.service';
 
import {
  ConfirmOrderForOverview,
  CreateOrder,
  CreateOrderSuccess,
  DeleteOrderLine,
  FertilisersActionTypes,
  GetArticlesByCropAidsSuccess,
  GetFertiliserOrderplanSuccess,
  SaveCustomerFertilizerStock,
  SetCustomerFertilizerStock,
  ShowFertilizersOrderDialog,
  SaveOrderLine,
  OptimizeOrderLine,
  GetFertiliserOrderplan,
  SilentlyOptimizeOrderLine,
  SilentlyGetFertiliserOrderplan,
} from '../actions/fertilisers-actions';
import { selectFertiliserOrderplan } from '../selectors/fertiliser.selectors';

@Injectable()
export class FertilisersEffects {
  protected store = inject<Store<AppState>>(Store);
  protected actions$ = inject(Actions);
  protected fertilisersApi = inject(FertilisersApiService);
  private readonly router = inject(Router);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly env = inject<Environment>(EnvironmentInjector);

  getFertiliserOrderplan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.GetFertiliserOrderplan),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.fertilisersApi.getFertiliserOrderplan(year).pipe(
            map((orderplan) => new GetFertiliserOrderplanSuccess(orderplan)),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  silentlyGetFertiliserOrderplan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.SilentlyGetFertiliserOrderplan),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.fertilisersApi.getFertiliserOrderplan(year).pipe(
            map((orderplan) => new GetFertiliserOrderplanSuccess(orderplan)),
            startWith(CoreActions.setLoading({ loading: false })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  getArticlesByCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.GetArticlesByCropAids),
      withLatestFrom(this.store.pipe(select(selectFertiliserOrderplan))),
      switchMap(([_, fertiliserOrderplan]) => {
        if (fertiliserOrderplan) {
          const cropAidIds = fertiliserOrderplan.map((orderplan) => orderplan.cropAidId);
          if (cropAidIds?.length > 0) {
            return this.fertilisersApi.getArticlesByCropAids(cropAidIds).pipe(
              map((cropAids) => new GetArticlesByCropAidsSuccess(cropAids)),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            );
          }
          return EMPTY;
        }
        return EMPTY;
      }),
    ),
  );

  createOrderReceiptReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FertilisersActionTypes.CreateOrderReceiptReport),
        map(() => this.router.navigate(['fertilizerOrders/report'])),
      ),
    { dispatch: false },
  );

  confirmOrderForOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.ConfirmOrderForOverview),
      withLatestFrom(this.store.pipe(select(selectFertiliserOrderplan))),
      switchMap(([_, fertiliserOrderplan]: [ConfirmOrderForOverview, FertiliserOrderplan[]]) => {
        if (fertiliserOrderplan.length === 0) {
          return this.dialog
            .notify({
              title: this.locale.translate('orderTexts.noItemsInBasketTitle'),
              text: this.locale.translate('orderTexts.noItemsInBasketDescription'),
            })
            .pipe(switchMap((__) => EMPTY));
        } else if (!this.env.ENABLE_SEND_ORDER) {
          return this.dialog
            .notify({
              title: this.locale.translate('orderTexts.customerNotAllowedToOrderTitle'),
              text: this.locale.translate('orderTexts.sendOrderIgnored'),
            })
            .pipe(switchMap((__) => EMPTY));
        } else {
          return of(new ShowFertilizersOrderDialog());
        }
      }),
    ),
  );

  showFertilizersOrderDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.ShowFertilizersOrderDialog),
      withLatestFrom(this.store.pipe(select(selectFertiliserOrderplan))),
      switchMap(([_, fertiliserOrderplans]: [ShowFertilizersOrderDialog, FertiliserOrderplan[]]) => {
        const fertilisersThatCanBeOrdered = fertiliserOrderplans.filter((x) => x.amount > 0);
        return this.dialog
          .fullscreenDialog(ConfirmFertilisersOrderDialogComponent, {
            fertiliserOrderplans: fertilisersThatCanBeOrdered,
            title: this.locale.translate('fertilisers-overview.confirmation-dialog-title'),
            confirmButton: true,
            confirmButtonText: this.locale.translate('orderTexts.sendOrder'),
          } as ConfirmFertilisersOrderDialogData)
          .pipe(
            switchMap(({ status, result }) => {
              switch (status) {
                case DIALOG_RETURN_TYPES.cancel:
                  return EMPTY;
                case DIALOG_RETURN_TYPES.confirm:
                  return of(new CreateOrder(result));
              }
            }),
          );
      }),
    ),
  );

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.CreateOrder),
      withLatestFrom(
        this.store.pipe(select(getYear)),
        this.store.pipe(select(UserSelectors.selectCustomer)),
        this.store.pipe(select(UserSelectors.selectLocation)),
      ),
      switchMap(([_, year, customer, location]: [CreateOrder, number, SelectedCustomer, SelectedLocation]) =>
        customer.status === CustomerStatus.Customer && !customer.orderBlock
          ? this.fertilisersApi.createOrder(year, _.fertiliserOrderFormParameters).pipe(
              map(() => new CreateOrderSuccess(customer.id, location.id)),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            )
          : this.dialog
              .notify({
                title: this.locale.translate('orderTexts.customerNotAllowedToOrderTitle'),
                text: this.locale.translate('orderTexts.customerNotAllowedToOrderText'),
              })
              .pipe(switchMap(() => EMPTY)),
      ),
    ),
  );

  createOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.CreateOrderSuccess),
      switchMap(({ locationId, customerId }) =>
        this.env.ENABLE_SEND_ORDER
          ? this.dialog
              .confirm({
                title: this.locale.translate('orderTexts.successfullyOrdered'),
                confirmButtonText: this.locale.translate('openCart'),
                cancelButtonText: this.locale.translate('returnToOverview'),
              })
              .pipe(
                switchMap(({ status }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.cancel:
                      return of(getCustomerData());
                    case DIALOG_RETURN_TYPES.confirm: {
                      const returnUrl = window.location.href;
                      return of(CartActions.openCartUrl({ customerId, locationId, returnUrl }));
                    }
                  }
                }),
              )
          : this.dialog
              .notify({
                title: this.locale.translate('orderTexts.sendOrderIgnored'),
              })
              .pipe(map(() => getCustomerData())),
      ),
    ),
  );

  getCustomerFertilizerStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.GetCustomerFertilizerStock),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, cropYear]) =>
        this.fertilisersApi.getCustomerFertilizerStock(cropYear).pipe(
          mergeMap((result) => of(new SetCustomerFertilizerStock(result))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  saveCustomerFertilizerStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.SaveCustomerFertilizerStock),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ fertilizerStockRows, orderLineId }, currentYear]: [SaveCustomerFertilizerStock, number]) =>
        this.fertilisersApi.upsertCustomerFertilizerStock(currentYear, fertilizerStockRows).pipe(
          switchMap(() => {
            if (orderLineId) {
              return of(new SilentlyOptimizeOrderLine(orderLineId));
            } else {
              return of(new SilentlyGetFertiliserOrderplan());
            }
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.DeleteOrderLine),
      switchMap(({ id }: DeleteOrderLine) =>
        this.fertilisersApi.deleteOrderLine(id).pipe(
          switchMap((_) => [getCustomerData()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.DeleteOrderLines),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ ids }, year]) =>
        this.fertilisersApi.deleteOrderLines(ids, year).pipe(
          map(() => getCustomerData()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.SaveOrderLine),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ orderLine, cropAidId: cropAidId }, currentYear]: [SaveOrderLine, number]) =>
        this.fertilisersApi.upsertOrderLine(currentYear, orderLine, cropAidId).pipe(
          map(() => getCustomerData()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  optimizeOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.OptimizeOrderLine),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ orderLineId }, currentYear]: [OptimizeOrderLine, number]) =>
        this.fertilisersApi.optimizeOrderLine(currentYear, orderLineId).pipe(
          switchMap(() => of(new GetFertiliserOrderplan())),
          catchError((_) => of(NotificationActions.errorSnackbar({ message: this.locale.translate('fertilisers-overview.optimizationError') }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  silentlyOptimizeOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilisersActionTypes.SilentlyOptimizeOrderLine),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ orderLineId }, currentYear]: [OptimizeOrderLine, number]) =>
        this.fertilisersApi.optimizeOrderLine(currentYear, orderLineId).pipe(
          switchMap(() => of(new SilentlyGetFertiliserOrderplan())),
          catchError((_) => of(NotificationActions.errorSnackbar({ message: this.locale.translate('fertilisers-overview.optimizationError') }))),
          startWith(CoreActions.setLoading({ loading: false })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
